import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Dropdown, Form, FloatingLabel, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { buildPrintDocument, openPrintWindow, buildLeaveAllotmentChart, buildChartItem } from '../utils/printUtility';

const AttendanceSummaryModal = ({ show, onHide, session, attendanceData, userData, isDirector, onAttendanceUpdated }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editStatus, setEditStatus] = useState('attended');
  const [editRecordedAt, setEditRecordedAt] = useState('');
  const [editRecordedAtTime, setEditRecordedAtTime] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  if (!session) return null;

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayMinute = minute.toString().padStart(2, '0');
      return `${displayHour}:${displayMinute} ${period}`;
    } catch (e) {
      console.error('Error formatting time:', e);
      return timeString;
    }
  };

  // Calculate attendance summary
  const requiredAttendees = session.requiredAttendees || [];
  const recordedAttendance = attendanceData[session.id]?.attendance || session.attendance || [];
  
  // Calculate required attendee stats
  const requiredAttendeeIds = new Set(requiredAttendees);
  const requiredAttendance = recordedAttendance.filter(r => requiredAttendeeIds.has(r.user.id));
  const requiredAttended = requiredAttendance.filter(r => r.status === 'attended').length;
  const requiredNotAttended = requiredAttendance.filter(r => r.status === 'not_attended').length;
  const requiredPending = requiredAttendees.length - requiredAttended - requiredNotAttended;
  
  // Calculate non-required attendee stats
  const nonRequiredAttendance = recordedAttendance.filter(r => !requiredAttendeeIds.has(r.user.id));
  const nonRequiredAttended = nonRequiredAttendance.filter(r => r.status === 'attended').length;
  const nonRequiredNotAttended = nonRequiredAttendance.filter(r => r.status === 'not_attended').length;
  const nonRequiredPending = nonRequiredAttendance.filter(r => r.status === 'pending').length;

  // Create attendance map
  const attendanceMap = new Map();
  recordedAttendance.forEach(record => {
    if (record.user) {
      attendanceMap.set(record.user.id, record);
    }
  });

  // Get all recorded attendee IDs
  const recordedAttendeeIds = Array.from(attendanceMap.keys());
  
  // Separate required and non-required attendees
  const nonRequiredAttendeeIds = recordedAttendeeIds.filter(id => !requiredAttendeeIds.has(id));

  const requiredTotal = requiredAttendees.length;
  const optionalTotal = nonRequiredAttendance.length;

  const requiredAttendedCountLabel = `${requiredAttended} / ${requiredTotal}`;
  const requiredAttendedPercentLabel = `${requiredTotal > 0 ? Math.round((requiredAttended / requiredTotal) * 100) : 0}%`;

  const requiredNotAttendedCountLabel = `${requiredNotAttended} / ${requiredTotal}`;
  const requiredNotAttendedPercentLabel = `${requiredTotal > 0 ? Math.round((requiredNotAttended / requiredTotal) * 100) : 0}%`;

  const requiredPendingCountLabel = `${requiredPending} / ${requiredTotal}`;
  const requiredPendingPercentLabel = `${requiredTotal > 0 ? Math.round((requiredPending / requiredTotal) * 100) : 0}%`;

  const optionalAttendedCountLabel = `${nonRequiredAttended} / ${optionalTotal}`;
  const optionalAttendedPercentLabel = `${optionalTotal > 0 ? Math.round((nonRequiredAttended / optionalTotal) * 100) : 0}%`;

  const handlePrint = () => {
    const sessionTitle = session.conferenceTitle || session.talkTitle || session.title || 'Conference Session';

    const eventDateLabel = session.date
      ? new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : 'Date not specified';

    const timeLabel = session.startTime && session.endTime
      ? `${formatTime(session.startTime)} - ${formatTime(session.endTime)}`
      : session.startTime
        ? `${formatTime(session.startTime)}`
        : 'Time not specified';

    const sessionDetailsSection = `
      <div class="print-section">
        <h2>Session Details</h2>
        <div style="display: flex; flex-wrap: wrap; justify-content: space-between; font-size: 12px; line-height: 1.4;">
          <div style="flex: 1; min-width: 220px; margin-bottom: 8px;">
            <strong>Event Time:</strong><br/>
            ${eventDateLabel} at ${timeLabel}
          </div>
          <div style="flex: 1; min-width: 180px; margin-bottom: 8px;">
            <strong>Talk Time:</strong><br/>
            ${session.talkTime || 'N/A'}
          </div>
          <div style="flex: 1; min-width: 180px; margin-bottom: 8px;">
            <strong>Location:</strong><br/>
            ${session.location || 'N/A'}
          </div>
        </div>
        <div style="display: flex; flex-wrap: wrap; justify-content: space-between; font-size: 12px; line-height: 1.4; margin-top: 4px;">
          <div style="flex: 1; min-width: 260px; margin-bottom: 8px;">
            <strong>Talk Title:</strong><br/>
            ${session.talkTitle || 'N/A'}
          </div>
          <div style="flex: 1; min-width: 260px; margin-bottom: 8px;">
            <strong>Speaker:</strong><br/>
            ${session.speaker || 'N/A'}${session.speakerTitle ? ` (${session.speakerTitle})` : ''}
          </div>
        </div>
        ${session.description ? `
          <div style="margin-top: 10px; font-size: 12px; line-height: 1.4;">
            <strong>Description:</strong><br/>
            ${session.description}
          </div>
        ` : ''}
      </div>
    `;

    const summarySection = `
      <div class="print-section">
        <h2>Attendance Summary</h2>
        <div class="charts-container">
          ${buildChartItem({
            title: 'Required Attended',
            svgContent: buildLeaveAllotmentChart({
              used: requiredAttended,
              total: requiredTotal || 0,
              color: 'var(--green)'
            }),
            stats: [
              { label: 'Count', value: requiredAttendedCountLabel },
              { label: 'Percent', value: requiredAttendedPercentLabel }
            ]
          })}
          ${buildChartItem({
            title: 'Required Not Attended',
            svgContent: buildLeaveAllotmentChart({
              used: requiredNotAttended,
              total: requiredTotal || 0,
              color: 'var(--red)'
            }),
            stats: [
              { label: 'Count', value: requiredNotAttendedCountLabel },
              { label: 'Percent', value: requiredNotAttendedPercentLabel }
            ]
          })}
          ${buildChartItem({
            title: 'Required Pending',
            svgContent: buildLeaveAllotmentChart({
              used: requiredPending,
              total: requiredTotal || 0,
              color: 'var(--yellow)'
            }),
            stats: [
              { label: 'Count', value: requiredPendingCountLabel },
              { label: 'Percent', value: requiredPendingPercentLabel }
            ]
          })}
          ${buildChartItem({
            title: 'Optional Attended',
            svgContent: buildLeaveAllotmentChart({
              used: nonRequiredAttended,
              total: optionalTotal || 0,
              color: 'var(--indigo)'
            }),
            stats: [
              { label: 'Count', value: optionalAttendedCountLabel },
              { label: 'Percent', value: optionalAttendedPercentLabel }
            ]
          })}
        </div>
      </div>
    `;

    const rowsHtml = allRows.length === 0
      ? '<tr><td colspan="4" style="text-align:center; padding:20px;">No attendance records found</td></tr>'
      : allRows.map(row => {
          const requiredBadge = row.isRequired
            ? '<span class="badge badge-info" style="margin-left: 6px;">Required</span>'
            : '<span class="badge badge-warning" style="margin-left: 6px;">Optional</span>';

          let statusClass = 'badge-warning';
          if (row.status === 'attended') statusClass = 'badge-success';
          else if (row.status === 'not_attended') statusClass = 'badge-danger';

          const statusLabel = row.status === 'attended'
            ? 'Attended'
            : row.status === 'not_attended'
              ? 'Not Attended'
              : 'Pending';

          const recordedAtLabel = row.recordedAt
            ? new Date(row.recordedAt).toLocaleString()
            : '<span style="color:#666;">Not recorded</span>';

          return `
            <tr>
              <td>
                ${row.name}
                ${requiredBadge}
              </td>
              <td style="text-transform: capitalize;">
                ${row.role || ''}
              </td>
              <td>
                <span class="badge ${statusClass}">${statusLabel}</span>
              </td>
              <td>
                ${recordedAtLabel}
              </td>
            </tr>
          `;
        }).join('');

    const attendanceTableSection = `
      <div class="print-section">
        <h2>Attendance Details</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Recorded At</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </div>
    `;

    const content = `
      ${sessionDetailsSection}
      ${summarySection}
      ${attendanceTableSection}
    `;

    const generatedAt = new Date().toLocaleString();

    const printContent = buildPrintDocument({
      title: `Conference Attendance - ${sessionTitle}`,
      content,
      headerTitle: 'Conference Attendance Report',
      headerSubtitle: sessionTitle,
      logoUrl: null,
      showPrintButton: true,
      additionalHeaderInfo: [
        { label: 'Session', value: sessionTitle },
        { label: 'Event Date', value: eventDateLabel },
        { label: 'Generated', value: generatedAt }
      ],
      footerMessage: 'This is a system-generated report. For questions, please contact the program director.',
      customStyles: '',
      pageSize: 'landscape'
    });

    openPrintWindow(printContent);
  };

  const StatCircle = ({ label, value, total, ringColor }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    const trackColor = 'var(--gray-300)';
    const innerBg = 'var(--card-bg)';

    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-2">
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: '50%',
            background: `conic-gradient(${ringColor} ${pct}%, ${trackColor} 0)`,
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <div
            style={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              background: innerBg,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div className="fw-bold" style={{ lineHeight: 1 }}>
              {value}
            </div>
            <div className="text-muted" style={{ fontSize: '0.7rem', lineHeight: 1, marginTop: 4 }}>
              {total > 0 ? `${pct}%` : '—'}
            </div>
          </div>
        </div>
        <div className="mt-2 text-center">
          <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
            {label}
          </div>
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
            {total > 0 ? `${value}/${total}` : '0/0'}
          </div>
        </div>
      </div>
    );
  };

  // Create rows for required attendees
  const requiredRows = requiredAttendees.map((attendeeId, index) => {
    const attendanceRecord = attendanceMap.get(attendeeId);
    const status = attendanceRecord ? attendanceRecord.status : 'pending';
    const recordedAt = attendanceRecord?.recordedAt;
    const userInfo = userData[attendeeId];
    return {
      id: `required-${index}`,
      attendanceRecordId: attendanceRecord?.id,
      userId: attendeeId,
      name: userInfo ? 
        `${userInfo.firstName} ${userInfo.lastName}` : 
        attendanceRecord?.user ? 
          `${attendanceRecord.user.firstName} ${attendanceRecord.user.lastName}` : 
          `User ID: ${attendeeId}`,
      role: userInfo?.role || attendanceRecord?.user?.role || 'Unknown',
      status,
      recordedAt,
      isRequired: true
    };
  });

  // Create rows for non-required attendees
  const nonRequiredRows = nonRequiredAttendeeIds.map((attendeeId, index) => {
    const attendanceRecord = attendanceMap.get(attendeeId);
    const status = attendanceRecord ? attendanceRecord.status : 'pending';
    const recordedAt = attendanceRecord?.recordedAt;
    const userInfo = userData[attendeeId];
    return {
      id: `non-required-${index}`,
      attendanceRecordId: attendanceRecord?.id,
      userId: attendeeId,
      name: userInfo ? 
        `${userInfo.firstName} ${userInfo.lastName}` : 
        attendanceRecord?.user ? 
          `${attendanceRecord.user.firstName} ${attendanceRecord.user.lastName}` : 
          `User ID: ${attendeeId}`,
      role: userInfo?.role || attendanceRecord?.user?.role || 'Unknown',
      status,
      recordedAt,
      isRequired: false
    };
  });

  const allRows = [...requiredRows, ...nonRequiredRows];

  // Handle opening edit modal
  const handleEditClick = (row) => {
    setSelectedRecord(row);
    setEditStatus(row.status);
    
    // Set date and time from recordedAt if available
    if (row.recordedAt) {
      const date = new Date(row.recordedAt);
      const dateStr = date.toISOString().split('T')[0];
      const timeStr = date.toTimeString().split(' ')[0].substring(0, 5); // HH:MM format
      setEditRecordedAt(dateStr);
      setEditRecordedAtTime(timeStr);
    } else {
      // Default to current date/time
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].substring(0, 5);
      setEditRecordedAt(dateStr);
      setEditRecordedAtTime(timeStr);
    }
    
    setError('');
    setShowEditModal(true);
  };

  // Handle closing edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedRecord(null);
    setEditStatus('attended');
    setEditRecordedAt('');
    setEditRecordedAtTime('');
    setError('');
  };

  // Handle updating attendance
  const handleUpdateAttendance = async () => {
    if (!selectedRecord || !selectedRecord.attendanceRecordId) {
      setError('No attendance record selected');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      // Combine date and time
      const recordedAtDateTime = editRecordedAt && editRecordedAtTime 
        ? `${editRecordedAt}T${editRecordedAtTime}:00`
        : null;

      const response = await axios.put(
        `${API_ENDPOINTS.CONFERENCE_ATTENDANCE}/${selectedRecord.attendanceRecordId}`,
        {
          status: editStatus,
          recordedAt: recordedAtDateTime
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data) {
        // Call the callback to refresh attendance data
        if (onAttendanceUpdated) {
          onAttendanceUpdated();
        }
        handleCloseEditModal();
      }
    } catch (err) {
      console.error('Error updating attendance:', err);
      setError(err.response?.data?.error || 'Failed to update attendance');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100 d-flex flex-row justify-content-between align-items-center">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Attendance Summary</span>
              <div className="text-hl"></div>
            </div>
            <Button variant="outline-primary me-4" size="sm" onClick={handlePrint}>
                <i className="bi bi-printer"></i>
                <span className="d-none d-md-inline ms-2">Print Report</span>
            </Button>

          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-0">
          <div className="mb-3 px-md-4 py-2">
            <div className="row">
              <div className="col-md-3 col-6 mb-2 mb-md-0">
                <div className="rounded d-flex justify-content-center">
                  <StatCircle
                    label="Required Attended"
                    value={requiredAttended}
                    total={requiredTotal}
                    ringColor="var(--green)"
                  />
                </div>
              </div>
              <div className="col-md-3 col-6 mb-2 mb-md-0">
                <div className="bg-light rounded d-flex justify-content-center">
                  <StatCircle
                    label="Required Not Attended"
                    value={requiredNotAttended}
                    total={requiredTotal}
                    ringColor="var(--red)"
                  />
                </div>
              </div>
              <div className="col-md-3 col-6 mb-2 mb-md-0">
                <div className="bg-light rounded d-flex justify-content-center">
                  <StatCircle
                    label="Required Pending"
                    value={requiredPending}
                    total={requiredTotal}
                    ringColor="var(--yellow)"
                  />
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="bg-light rounded d-flex justify-content-center">
                  <StatCircle
                    label="Optional Attended"
                    value={nonRequiredAttended}
                    total={optionalTotal}
                    ringColor="var(--blue)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tableFixHead border-top">
          <Table responsive hover className="mb-0">
            <thead>
              <tr className="table-light">
                <th className="ps-4">Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Recorded At</th>
                {isDirector && <th className="text-end pe-4">Actions</th>}
              </tr>
            </thead>
            
            <tbody>
              {allRows.map((row) => (
                <tr key={row.id}>
                  <td className="ps-4">
                    {row.name}
                    <Badge 
                      bg={row.isRequired ? "primary" : "info"} 
                      className="ms-2" 
                      style={{fontSize: '0.7rem'}}
                    >
                      {row.isRequired ? <i className="bi bi-person-check"></i> : "optional"}
                    </Badge>
                  </td>
                  <td className="text-capitalize">
                    {row.role}
                  </td>
                  <td>
                    <Badge 
                      bg={
                        row.status === 'attended' ? 'success' : 
                        row.status === 'not_attended' ? 'danger' : 
                        'warning'
                      }
                    >
                      {row.status === 'attended' ? 'Attended' :
                     row.status === 'not_attended' ? 'Not Attended' :
                     'Pending'}
                    </Badge>
                  </td>
                  <td>
                    {row.recordedAt ? 
                      new Date(row.recordedAt).toLocaleString() : 
                      <span className="text-muted">Not recorded</span>
                    }
                  </td>
                  {isDirector && (
                    <td className="text-end pe-4">
                      {row.attendanceRecordId && (
                        <Dropdown>
                          <Dropdown.Toggle 
                            variant="light" 
                            size="sm"
                            className="no-arrow"
                            id={`dropdown-${row.id}`}
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => handleEditClick(row)}>
                              <i className="bi bi-pencil me-2"></i>
                              Edit Status
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          </div>
  
          {allRows.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 mb-3 d-block"></i>
              No attendance records found
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer className="border-top px-4">
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Attendance Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton className="border-bottom-0">
          <Modal.Title className="d-flex flex-column justify-content-start align-items-start w-100">
            <span>Edit Attendance</span>
            <div className="text-hl"></div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <>
              <div className="mb-3">
                <strong>Name:</strong> {selectedRecord.name}
              </div>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              <Form.Group className="mb-3">
                <FloatingLabel label="Status">
                  <Form.Select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    disabled={updating}
                  >
                    <option value="attended">Attended</option>
                    <option value="not_attended">Not Attended</option>
                    <option value="pending">Pending</option>
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <FloatingLabel label="Date Recorded">
                    <Form.Control
                      type="date"
                      value={editRecordedAt}
                      onChange={(e) => setEditRecordedAt(e.target.value)}
                      disabled={updating}
                    />
                  </FloatingLabel>
                </div>
                <div className="col-md-6 mb-3">
                  <FloatingLabel label="Time Recorded">
                    <Form.Control
                      type="time"
                      value={editRecordedAtTime}
                      onChange={(e) => setEditRecordedAtTime(e.target.value)}
                      disabled={updating}
                    />
                  </FloatingLabel>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="secondary" size="sm" onClick={handleCloseEditModal} disabled={updating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateAttendance} disabled={updating}>
            {updating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              'Update'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttendanceSummaryModal;
